import { motion } from "framer-motion";
import { Car, Cog, Factory, ShoppingCart, Users } from "lucide-react";
import { majorCustomers } from "@/data/content";

const icons = { car: Car, cog: Cog, factory: Factory, cart: ShoppingCart };

export const ClientsSection = () => {
    return (
        <section className="relative py-24 sm:py-32 bg-slate-900 text-white overflow-hidden" data-testid="clients-section">
            <div className="absolute inset-0 bg-dot-slate opacity-[0.07]" />
            <div className="absolute -top-32 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-16">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow mb-4">
                        Our Clients
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                        Major <span className="text-brand-yellow">Customers</span>
                    </h2>
                    <p className="mt-4 text-slate-400 max-w-2xl leading-relaxed">
                        Serving India&apos;s leading OEMs, Tier-1 suppliers, industrial
                        manufacturers &amp; e-commerce companies.
                    </p>
                </div>

                <div className="space-y-14">
                    {majorCustomers.map((group, gi) => {
                        const Icon = icons[group.icon];
                        return (
                            <div key={group.category} data-testid={`client-group-${group.icon}`}>
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                                        <Icon className="w-4.5 h-4.5 text-white" strokeWidth={2} />
                                    </div>
                                    <h3 className="font-display text-sm sm:text-base font-black uppercase tracking-[0.15em] text-brand-yellow">
                                        {group.category}
                                    </h3>
                                    <div className="flex-1 h-px bg-gradient-to-r from-brand-yellow/50 to-transparent" />
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                    {group.clients.map((c, i) => (
                                        <motion.div
                                            key={c.name}
                                            initial={{ opacity: 0, y: 16 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.4) }}
                                            className="h-20 rounded-xl bg-white flex items-center justify-center px-4 shadow-lg hover:-translate-y-1 hover:shadow-xl transition-transform duration-300"
                                            title={c.name}
                                        >
                                            {c.src ? (
                                                <img
                                                    src={c.src}
                                                    alt={c.name}
                                                    loading="lazy"
                                                    className="max-h-10 w-auto"
                                                />
                                            ) : (
                                                <span className="font-display font-bold text-base text-slate-800 tracking-tight text-center leading-tight">
                                                    {c.name}
                                                </span>
                                            )}
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-16 flex items-center justify-center gap-3 py-4 rounded-2xl bg-blue-600/20 border border-blue-500/20">
                    <Users className="w-5 h-5 text-brand-yellow" />
                    <span className="font-display font-bold text-sm sm:text-base tracking-tight">
                        Trusted by <span className="text-brand-yellow">100+</span> Leading
                        Customers Across India
                    </span>
                </div>
            </div>
        </section>
    );
};

export default ClientsSection;
